import axios from "axios";
import { get, each } from "lodash";
import { toastr } from "react-redux-toastr";
import { Group, error, warn } from "../../utils/logger";
import { setAuthToken } from "../../utils/axiosConfig";
import { storeAdminUser, getUsers } from "./adminActions";

import {
  USER_LOADING,
  USER_AVAILABLE,
  LOAD_USER,
  REGISTER_USER,
  LOGIN_USER,
  SSO_USER,
  LOGOUT_USER,
  DELETE_USER,
  UPDATE_USER,
  AUTH_ERROR,
  USER_DESIRED_PAGE,
  USER_TOKEN_NAME,
  ADMIN_TOKEN_NAME,
  INTERNAL_TOKEN_NAME,
} from "../constants";

const LoggerGroup = new Group("User Actions", { disable: true });

// ================================================================================================
// Convenience Operations =========================================================================
// ================================================================================================

export const saveDesiredPage = () => (dispatch) => {
  const desired_page = window.location.pathname + window.location.search + window.location.hash;

  sessionStorage.setItem(USER_DESIRED_PAGE, desired_page);

  LoggerGroup.emit(`Unauthenticated user attempted to visit url[${desired_page}] - saved`).end();
};

export const getDesiredPage = () => () => {
  const storedPage = sessionStorage.getItem(USER_DESIRED_PAGE);
  const desired_page = storedPage || "/dashboard/campaigns";

  if (storedPage) {
    LoggerGroup.emit(`Found stored page - removing`);
    sessionStorage.removeItem(USER_DESIRED_PAGE);
  }

  LoggerGroup.emit(`Redirecting to url[${desired_page}]`).end();

  return desired_page;
};

// ================================================================================================
// User Operations ================================================================================
// ================================================================================================

/**
 * Check if the provided email address is still available
 * @param {Object} user
 */
export const checkIfUserAvailable = (user) => (dispatch) => {
  LoggerGroup.emit("[i] Checking if user available");

  axios
    .post("user/check-availability", user)
    .then((res) => dispatch({ type: USER_AVAILABLE, payload: !res.data.user_exists }))
    .catch((err) => {
      const errors = get(err, "response.data.errors");
      dispatch({ type: AUTH_ERROR, payload: errors });
    });
};

// ================================================================================================
// Password Operations ============================================================================
// ================================================================================================

/**
 * Send password reset email
 * @param {string}  email   Email Address of the user to be reset
 */
export const requestResetPassword = (email) => (dispatch) => {
  LoggerGroup.emit(`[i] Resetting password for User[${email}]`);

  return axios
    .post("user/reset-password", { email })
    .then(() => {
      LoggerGroup.emit(`  [i] Password reset for user[${email}]`);
      return true;
    })
    .catch((err) => {
      const message = get(err, "response.data.error");
      LoggerGroup.emit(
        { warn: `[i] Error resetting password for user[${email}]` },
        { dir: err.response }
      ).end();

      toastr.error("Oops", message);
      dispatch({ type: AUTH_ERROR, payload: { reset_password: message } });
    });
};

/**
 * Allow a user to reset their password without logging in
 * @param {object}  user   Email Address of the user to be reset
 */
export const resetPassword = (user) => (dispatch) => {
  LoggerGroup.emit(`[i] Resetting password`);

  return axios
    .patch("user/reset-password", user)
    .then((res) => {
      LoggerGroup.emit(`  [i] Password`);
      dispatch({ type: LOGIN_USER, payload: res.data }); // Save token to state
      dispatch(loadUser(res.data)); // Get user information
      return res.data;
    })
    .catch((err) => {
      const message = get(err, "response.data.error");
      LoggerGroup.emit({ warn: `[i] Error resetting password` }, { dir: err.response }).end();

      toastr.error("Oops", message);
      dispatch({ type: AUTH_ERROR, payload: { reset_password: message } });
    });
};

/**
 * Get user record and apply to the state
 * @param {Object} payload
 * @param {String} payload.token  Authentication Token (JWT)
 * @param {String} payload.from   "token" || "memory"
 */
export const loadUser = (payload) => (dispatch, getState) => {
  const { selectedUser } = getState().admin;

  LoggerGroup.emit("[i] Saving token to request library");

  dispatch({ type: USER_LOADING });

  const user_token =
    localStorage.getItem(ADMIN_TOKEN_NAME) || localStorage.getItem(USER_TOKEN_NAME);

  setAuthToken(payload.token, user_token);

  axios
    .get("user")
    .then((res) => {
      const user = get(res, "data.user");
      dispatch({ type: LOAD_USER, payload: res.data });

      if (/^.+@messagebroadcast.com$/.test(user.email_vch) || !user.email_vch.includes("@")) {
        sessionStorage.setItem(INTERNAL_TOKEN_NAME, "true");
      }

      if (!selectedUser.user_id_bint && user.group_id_bint > 2) {
        dispatch(storeAdminUser({ token: payload.token, selectedUser: user }));
      }
    })
    .catch((err) => {
      // Ensure any invalid saved values are cleared on error (expired, invalid, etc)
      localStorage.removeItem(USER_TOKEN_NAME);
      localStorage.removeItem(ADMIN_TOKEN_NAME);

      toastr.error("There was an issue with our service, please wait a few moments");
      error("loadUser", err);

      const errors = get(err, "response.data.errors");
      dispatch({ type: AUTH_ERROR, payload: errors });
    });
};

/**
 * Register a newUser to the service
 * @param {Object}  newUser
 * @param {String}  newUser.email
 * @param {String}  newUser.password
 */
export const registerUser = (newUser) => (dispatch, getState) => {
  const { user } = getState().user;

  LoggerGroup.emit("[i] Registering newUser", { table: newUser });

  dispatch({ type: USER_LOADING });

  axios
    .put("auth/register", newUser)
    .then((res) => {
      LoggerGroup.emit("[i] User registered", { table: res.data }).end();

      // Admin newUser creating other users
      if (user.group_id_bint > 2) {
        dispatch(getUsers(true));
        dispatch({ type: USER_LOADING, payload: false });
      } else {
        // User self-registering
        dispatch({ type: REGISTER_USER, payload: res.data }); // Save token to state
        dispatch(loadUser(res.data)); // Get User information
      }
    })
    .catch((err) => {
      LoggerGroup.emit({ warn: "[i] Error registering newUser" }, { dir: err.response }).end();

      const errors = get(err, "response.data.errors");
      dispatch({ type: AUTH_ERROR, payload: errors });

      if (errors) {
        each(errors, (error_message, type) => toastr.error(`${type} error`, error_message));
      }
    });
};

/**
 * Attempt to log in a user
 * @param {Object}  user
 * @param {String}  user.email
 * @param {String}  user.password
 * @param {Boolean} user.rememberMe
 */
export const signInUser = (user) => async (dispatch) => {
  LoggerGroup.emit("[i] Logging in user", { table: user });

  if (!user.email || !user.password) {
    return toastr.error(
      "Oops",
      `Looks like you forgot to enter your ${user.email ? "password" : "email"}`
    );
  }

  dispatch({ type: USER_LOADING });

  axios
    .post("auth/log-in", user)
    .then((res) => {
      if (res.status === 200) {
        res.data.rememberMe = user.rememberMe;
        LoggerGroup.emit("  [i] User logged in", { table: res.data }).end();

        dispatch({ type: LOGIN_USER, payload: res.data }); // Save token to state
        return dispatch(loadUser(res.data)); // Get user information
      }

      LoggerGroup.emit("  [x] Error logging in user").end();
      dispatch({ type: AUTH_ERROR, payload: { user: "Unable to authenticate user" } });
    })
    .catch((err) => {
      LoggerGroup.emit({ warn: "[i] Error logging in user" }, { dir: err.response }).end();

      let message = "Looks like that information was incorrect. Please double check and try again";
      const errors = get(err, "response.data.errors");

      try {
        message = Object.values(errors)[0];
      } catch (ex) {
        // Use default
      }
      toastr.error("Oops", message);

      dispatch({ type: AUTH_ERROR, payload: errors });
    });
};

/**
 * Attempt to log in a user using SSO
 * @param {Object}  sso
 * @param {String}  sso.type
 * @param {String}  sso.authData
 */
let ssoInProgress = false;
export const ssoUser = (sso) => async (dispatch) => {
  LoggerGroup.emit("[i] Logging in user through SSO", { table: sso });

  if (ssoInProgress) {
    warn("TRIED AGAIN!?");
    return Promise.resolve("In process");
  }
  ssoInProgress = true;

  if (!sso.type || !sso.authData) {
    return toastr.error("Oops", `There was an issue trying to sign you in using your provider`);
  }

  dispatch({ type: SSO_USER });

  return axios
    .post("auth/sso", sso)
    .then((res) => {
      if (res.status === 200) {
        LoggerGroup.emit("  [i] User logged in", { table: res.data }).end();

        dispatch({ type: LOGIN_USER, payload: res.data }); // Save token to state
        return dispatch(loadUser(res.data)); // Get user information
      }

      LoggerGroup.emit("  [x] Error logging in user").end();
      dispatch({ type: AUTH_ERROR, payload: { user: "Unable to authenticate user" } });
    })
    .catch((err) => {
      LoggerGroup.emit({ warn: "[i] Error logging in user" }, { dir: err.response }).end();

      let message =
        "There was an issue with that provider. Please notify the support team for assistance";
      const errors = get(err, "response.data.errors");

      try {
        message = Object.values(errors)[0];
      } catch (ex) {
        // Use default
      }
      toastr.error("Oops", message);

      dispatch({ type: AUTH_ERROR, payload: errors });
    })
    .finally(() => {
      ssoInProgress = false;
    });
};

/**
 * Logs a user out by removing user information from the state
 */
export const logoutUser = () => (dispatch) => {
  toastr.confirm("Are you sure you want to log out?", {
    okText: "Sign Out",
    cancelText: "Cancel",
    onCancel: () => LoggerGroup.emit("Cancelled logout").end(),
    onOk: () => {
      dispatch({ type: LOGOUT_USER });
      dispatch({ type: "RESET_APP" });

      toastr.success("Success", "You have been logged out");
    },
  });
};

export const updateUser = (user) => (dispatch) => {
  LoggerGroup.emit("[i] Updating user", { table: user });

  axios
    .patch("user", user)
    .then((res) => {
      toastr.success("Success", "Your profile has been updated");
      dispatch({ type: UPDATE_USER, payload: res.data }); // Update token if saved
      dispatch(loadUser(res.data)); // Get user information
    })
    .catch((err) => {
      const message =
        get(err, "response.data.error") || "There was a problem updating your profile";
      LoggerGroup.emit({ warn: "[i] Error deleting user" }, { dir: err.response }).end();
      toastr.error("Oops", message);

      dispatch({ type: AUTH_ERROR, payload: { update: message } });
    });
};

/**
 * Delete a user from the service
 * @param {Object}  user
 * @param {String}  user.email
 * @param {String}  user.password
 */
export const deleteUser = (user) => (dispatch, getState) => {
  LoggerGroup.emit("[i] Deleting user", { table: user });

  const this_user = getState().user.user;

  axios
    .delete("auth/delete", user)
    .then((res) => {
      LoggerGroup.emit("  [i] User deleted", { table: res.data });
      dispatch({ type: DELETE_USER });

      if (this_user.user_id_bint === user.user_id_bint) {
        LoggerGroup.emit("  [i] THIS user deleted - proceeding to log out");
        dispatch({ type: LOGOUT_USER });
        dispatch({ type: "RESET_APP" });
      }

      toastr.light("You have successfully deleted the account", {
        icon: "success",
        status: "success",
      });
      LoggerGroup.end();
    })
    .catch((err) => {
      LoggerGroup.emit({ warn: "[i] Error deleting user" }, { dir: err.response }).end();
      dispatch({ type: AUTH_ERROR, payload: { delete: "Error deleting user" } });
    });
};

/*============================================
SSO Saml Check for SAML Authentication Backend
==============================================*/
export const loginByJwt = (jwtToken) => (dispatch) => {
  LoggerGroup.emit(`Logging in with JWT`, { table: [{ jwtToken }] });

  return axios
    .get("auth/jwt", {
      headers: {
        authorization: "Bearer " + jwtToken,
      },
    })
    .then(handleSuccess)
    .catch(handleError);

  function handleSuccess(res) {
    dispatch({ type: LOGIN_USER, payload: res.data });
    dispatch(loadUser(res.data));
    LoggerGroup.end();
  }

  function handleError(ex) {
    LoggerGroup.emit({ warn: "No SSO Login Detected" }) // Custom error log
      .emit({ error: ex }) // Log the Axios Error
      .end(); // Send to console immediately
    dispatch({ type: AUTH_ERROR, payload: ex });
    LoggerGroup.end();
  }
};
